import {
  TellescopeCreateRequestFromName,
  TellescopeModelFromName,
  useAuth,
  useHealthiePatientsQuery,
  useTellescopeInfiniteQuery,
  useTellescopeMutation,
} from "@tellescope/tanstack-query";
import {
  Button,
  cn,
  DialogFooter,
  DialogSection,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@tellescope/ui";
import { useDebouncedValue } from "@tellescope/react-hooks";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { IntegrationName } from "@tellescope/tanstack-query";
import type { SetupNotification } from "../../../../setup-flow";
import { StepWrapper } from "./StepWrapper";
import { formatName, formatPhone } from "@tellescope/utils";

const normalizeDOB = (dob: string | Date | null): TellescopeModelFromName<"endusers">["dateOfBirth"] => {
  if (!dob) return undefined;
  const [y, m, d] =
    typeof dob === "object" ? [dob.getFullYear(), dob.getMonth(), dob.getDate()].map(String) : dob.split("-");
  if (isNaN(Number(y)) || y?.length !== 4) return undefined;
  if (isNaN(Number(m)) || m?.length !== 2) return undefined;
  if (isNaN(Number(d)) || d?.length !== 2) return undefined;
  return [m, d, y].join("-");
};

const VALID_GENDERS = [
  "Male",
  "Other",
  "Unknown",
  "Female",
] as const satisfies TellescopeModelFromName<"endusers">["gender"][];

const normalizeGender = (gender: string | null): TellescopeModelFromName<"endusers">["gender"] => {
  const match = VALID_GENDERS.find((item) => item === gender);
  return match;
};

const normalizeTimezone = (timezone: unknown | null): TellescopeModelFromName<"endusers">["timezone"] => {
  if (!timezone || typeof timezone !== "string") return undefined;
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return timezone as TellescopeModelFromName<"endusers">["timezone"];
  } catch {
    return undefined;
  }
};

const normalizeName = (value: string | null | undefined): string =>
  String(value ?? "")
    .trim()
    .toLowerCase();
const normalizeEmail = (value: string | null | undefined): string => (value ?? "").trim().toLowerCase();
const normalizePhone = (value: string | null | undefined): string => (value ?? "").replace(/\D/g, "");

export interface HealthiePatientImportStepProps {
  selectedIntegrationNames: IntegrationName[];
  onSkip?: () => void | Promise<void>;
  /** Advance past this step once the import finishes. */
  onComplete: () => void | Promise<void>;
  onBack?: () => void | Promise<void>;
  /** Surface a success notification to the host (e.g. a toast). */
  onSuccess?: (notification: SetupNotification) => void;
  /** Surface an error/warning notification to the host. */
  onError?: (notification: SetupNotification) => void;
}

export const HealthiePatientImportStep = ({
  selectedIntegrationNames,
  onSkip,
  onComplete,
  onBack,
  onSuccess,
  onError,
}: HealthiePatientImportStepProps) => {
  const { t } = useTranslation("onboarding");
  const { isAdmin } = useAuth();

  const patientsQuery = useHealthiePatientsQuery({
    request: {
      pageSize: 100,
    },
    fetchAllPages: true,
    maxAutomaticPageFetches: 20,
  });

  const patients = useMemo(() => patientsQuery.data?.patients ?? [], [patientsQuery.data?.patients]);

  // Only unlinked endusers (no externalId) are fetched — these are the candidates the webhook
  // would fuzzy-match against. Already-linked patients are left to the unique externalId
  // constraint on create. Soft cap: 1000 × 20 = ~20k unlinked endusers.
  const existingEndusersQuery = useTellescopeInfiniteQuery({
    model: "endusers",
    method: "getSome",
    request: { limit: 1000, mdbFilter: { externalId: { $exists: false } } },
    fetchAllPages: true,
    maxAutomaticPageFetches: 100,
    select: (data) => data.pages.flat(),
  });

  const isLoading =
    patientsQuery.isPending ||
    patientsQuery.hasNextPage ||
    existingEndusersQuery.isPending ||
    existingEndusersQuery.hasNextPage;
  const isError = patientsQuery.isError || existingEndusersQuery.isError;
  const [delayedIsLoading] = useDebouncedValue(isLoading, 1000);

  const createEndusersMutation = useTellescopeMutation({ model: "endusers", method: "createSome" });
  const updateEnduserMutation = useTellescopeMutation({ model: "endusers", method: "updateOne" });
  const [isImporting, setIsImporting] = useState(false);

  const unlinkedEndusersByName = useMemo(
    () =>
      (existingEndusersQuery.data ?? []).reduce<Map<string, TellescopeModelFromName<"endusers">[]>>((map, enduser) => {
        const fname = normalizeName(enduser.fname);
        const lname = normalizeName(enduser.lname);
        if (!fname || !lname) return map;

        const key = `${fname}|${lname}`;
        const group = map.get(key);
        if (group) group.push(enduser);
        else map.set(key, [enduser]);
        return map;
      }, new Map()),
    [existingEndusersQuery.data]
  );

  const { toCreate, toLink } = useMemo(
    () =>
      patients.reduce<{
        toCreate: TellescopeCreateRequestFromName<"endusers">[];
        toLink: { enduserId: string; request: TellescopeCreateRequestFromName<"endusers"> }[];
      }>(
        (acc, patient) => {
          if (patient.active === false) return acc; // Mirror webhook by skipping inactive/archived patients

          const fields: TellescopeCreateRequestFromName<"endusers">["fields"] = {
            ["Healthie Archived"]: "",
          };
          if (patient.group_name) fields["Healthie Group"] = patient.group_name;
          if (patient.additional_phone_number)
            fields["Healthie Additional Phone Number"] = patient.additional_phone_number;
          if (patient.additional_record_identifier)
            fields["Healthie Additional Record Identifier"] = patient.additional_record_identifier;

          const email = patient.email?.endsWith("@gethealthie.com") ? undefined : patient.email ?? undefined;
          const dateOfBirth = normalizeDOB(patient.dob);

          const request: TellescopeCreateRequestFromName<"endusers"> = {
            /* --- identity link (always need all 3) --- */
            source: "Healthie",
            externalId: patient.id ?? undefined,
            references: patient.id ? [{ type: "Healthie", id: patient.id }] : [],
            /* --- other fields --- */
            fname: patient.first_name ?? undefined,
            lname: patient.last_name ?? undefined,
            email,
            phone: patient.phone_number ?? undefined,
            dateOfBirth,
            gender: normalizeGender(patient.gender),
            timezone: normalizeTimezone(patient.timezone),
            addressLineOne: patient.location?.line1 ?? undefined,
            addressLineTwo: patient.location?.line2 ?? undefined,
            city: patient.location?.city ?? undefined,
            state: patient.location?.state ?? undefined,
            zipCode:
              typeof patient.location?.zip === "string"
                ? patient.location.zip
                : typeof patient.location?.zip === "number"
                ? String(patient.location.zip)
                : undefined,
            tags: patient.active_tags?.map(({ name }) => name),
            language:
              patient.preffered_language_code === "es-ES"
                ? {
                    displayName: "Spanish",
                    iso6391: "es-ES",
                  }
                : undefined,
            healthie_dietitian_id: patient.dietitian_id ?? undefined,
            fields,
          };

          // Mirror the webhook: match an existing unlinked enduser by fname+lname + (email OR dob OR phone)
          const fname = normalizeName(patient.first_name);
          const lname = normalizeName(patient.last_name);
          const normalizedEmail = normalizeEmail(email);
          const normalizedPhone = normalizePhone(patient.phone_number);
          const match =
            fname && lname
              ? unlinkedEndusersByName
                  .get(`${fname}|${lname}`)
                  ?.find(
                    (enduser) =>
                      (!!normalizedEmail && normalizeEmail(enduser.email) === normalizedEmail) ||
                      (!!dateOfBirth && enduser.dateOfBirth === dateOfBirth) ||
                      (!!normalizedPhone && normalizePhone(enduser.phone) === normalizedPhone)
                  )
              : undefined;

          if (match) acc.toLink.push({ enduserId: match.id, request });
          else acc.toCreate.push(request);
          return acc;
        },
        { toCreate: [], toLink: [] }
      ),
    [patients, unlinkedEndusersByName]
  );

  const onContinue = useCallback(async () => {
    setIsImporting(true);
    try {
      let imported = 0;
      let matched = 0;
      let errors = 0;

      // createSome isolates per-item (returns { created, errors }); it only throws when every
      // item conflicts — treat that as "all already synced" rather than a blocking failure.
      if (toCreate.length) {
        try {
          const result = await createEndusersMutation.mutateAsync({ items: toCreate });
          imported += result.created.length;
          errors += result.errors.length;
        } catch {
          errors += toCreate.length;
        }
      }

      // Link matched endusers individually so one failure can't block the rest.
      const linkResults = await Promise.allSettled(
        toLink.map(({ enduserId, request }) =>
          updateEnduserMutation.mutateAsync({
            id: enduserId,
            source: "Healthie",
            externalId: request.externalId,
            references: request.references,
          })
        )
      );
      matched += linkResults.filter((result) => result.status === "fulfilled").length;
      errors += linkResults.filter((result) => result.status === "rejected").length;

      await onComplete();

      const description = t("Imported {{imported}}, matched {{matched}}, {{errors}} errors", {
        imported,
        matched,
        errors,
      });
      if (errors > 0) {
        onError?.({ title: t("Patient import finished with errors"), description });
      } else {
        onSuccess?.({ title: t("Patients imported from Healthie"), description });
      }
    } finally {
      setIsImporting(false);
    }
  }, [createEndusersMutation, updateEnduserMutation, onComplete, onSuccess, onError, t, toCreate, toLink]);

  return (
    <>
      <StepWrapper selectedIntegrationNames={selectedIntegrationNames} highlightedIntegrations={["healthie"]}>
        <div className="min-h-0 pt-6 pb-4 flex flex-col gap-6" data-testid="onboarding-healthie-patients-step">
          <div className="space-y-2">
            <h3 className="text-xl font-semibold text-gray-900">{t("Healthie Integration Setup (2 of 2)")}</h3>
            <p className="text-sm text-gray-600">{t("Review the patients that will be synced from Healthie")}</p>
          </div>
          <div className="flex max-h-full flex-col overflow-hidden rounded-md border">
            <div className="flex-none overflow-hidden [scrollbar-gutter:stable]">
              <Table className="table-fixed">
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="w-[40%] py-4 px-6 uppercase text-xs text-muted-foreground">
                      {isLoading ? <Skeleton className="h-3 w-32 bg-gray-300" /> : t("Name")}
                    </TableHead>
                    <TableHead className="w-[35%] py-4 px-6 uppercase text-xs text-muted-foreground">
                      {isLoading ? <Skeleton className="h-3 w-24 bg-gray-300" /> : t("Email")}
                    </TableHead>
                    <TableHead className="w-[25%] py-4 px-6 uppercase text-xs text-muted-foreground">
                      {isLoading ? <Skeleton className="h-3 w-24 bg-gray-300" /> : t("Phone Number")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
              </Table>
            </div>
            <div className="min-h-0 overflow-y-auto [scrollbar-gutter:stable]">
              <Table className="table-fixed">
                <TableBody>
                  {isLoading ? (
                    Array.from({ length: 8 }, (_, index) => (
                      <TableRow key={index} className="bg-white">
                        <TableCell className="w-[40%] py-4 px-6">
                          <Skeleton className="h-4 w-44 bg-gray-300" />
                        </TableCell>
                        <TableCell className="w-[35%] py-4 px-6">
                          <Skeleton className="h-4 w-24 bg-gray-300" />
                        </TableCell>
                        <TableCell className="w-[25%] py-4 px-6">
                          <Skeleton className="h-4 w-24 bg-gray-300" />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : !patients.length ? (
                    <TableRow className="bg-white hover:bg-white">
                      <TableCell
                        colSpan={3}
                        className={cn(
                          "py-8 px-6 text-center text-sm",
                          isError ? "text-destructive" : "text-muted-foreground"
                        )}
                      >
                        {isError ? t("Failed to load patients from Healthie") : t("No patients found in Healthie")}
                      </TableCell>
                    </TableRow>
                  ) : (
                    patients.map(({ id, first_name, last_name, email, phone_number }) => {
                      const hasEmail = email && !email.endsWith("@gethealthie.com");

                      return (
                        <TableRow
                          key={id}
                          data-testid={`onboarding-patient-row-${id}`}
                          className="bg-white hover:bg-white"
                        >
                          <TableCell className="w-[40%] py-4 px-6 truncate">
                            {formatName({
                              fname: first_name ?? undefined,
                              lname: last_name ?? undefined,
                            })}
                          </TableCell>
                          <TableCell
                            className={cn("w-[35%] py-4 px-6 text-muted-foreground truncate", !hasEmail && "italics")}
                          >
                            {hasEmail ? email : "-"}
                          </TableCell>
                          <TableCell
                            className={cn("w-[25%] py-4 px-6 text-muted-foreground", !phone_number && "italics")}
                          >
                            {phone_number ? formatPhone(phone_number) : "-"}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </StepWrapper>
      <DialogSection className="p-0 border-none h-2 bg-gray-50">
        {(isLoading || delayedIsLoading) && (
          <div
            className={cn(
              "transition-all h-full w-full animate-gradient-shift bg-gradient-to-r from-blue-400 via-blue-600 to-blue-400 bg-[length:200%_100%] opacity-100",
              !isLoading && !!delayedIsLoading && "opacity-0"
            )}
            style={{ width: `${Math.max((patients.length / (patientsQuery.data?.count ?? Infinity)) * 100, 5)}%` }}
          />
        )}
      </DialogSection>
      <DialogFooter
        className={cn(
          "bg-gray-50 flex items-center justify-end py-4 px-8",
          onBack && "justify-between sm:justify-between"
        )}
      >
        {!!onBack && (
          <Button variant="outline" onClick={onBack} data-testid="onboarding-step-back">
            {t("Back")}
          </Button>
        )}
        <div className="flex items-center justify-end gap-2">
          {!!onSkip && (
            <Button variant="outline" onClick={onSkip} disabled={!isAdmin} data-testid="onboarding-step-skip">
              {t("I'll do this later")}
            </Button>
          )}
          <Button
            data-testid="onboarding-step-continue"
            onClick={onContinue}
            loading={isImporting}
            disabled={!isAdmin || isLoading || isError}
          >
            {t("Continue")}
          </Button>
        </div>
      </DialogFooter>
    </>
  );
};
